import { PlusIcon, DownloadIcon } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useCurrentUser, usePermissions } from '@/features/auth/state/auth'
import { useEffect, useState, type FormEvent } from 'react'
import { FeedbackDialog } from '@/components/form/feedback-dialog'
import { useUtils } from '@/lib/hooks/utils'
import { useToast } from '@/lib/hooks/use-toast'
import { handleDownloadPDFVerbas } from '@/components/form/gerador-pdf/verbas-diagnostico'
import { MaskInputDin } from '@/components/ui/mask-input-din'
import ButtonConfirmDelet from '@/components/ui/button-confirm-delet'
import {
  useCreateVerba,
  useDeleteVerba,
  useFetchVerbasByApuracaoCompetencia,
  useUpdateVerba,
  type Verba as VerbaApi
} from '../../services/useVerbas'
import { isAdmin } from '@/lib/permissions'

const verbaOptions = [
'Férias - Dobro na vigência do contrato',
'Licença-prêmio indenizada',
'Ressarcimento de despesas pelo uso de veículo próprio',
'Ressarcimento de despesas de viagem, exceto despesas com veículos',
'Ressarcimento de provisão',
'Ressarcimento de outras despesas',
'Reembolsos diversos',
'Danos e prejuízos causados pelo trabalhador',
'Juros e/ou atualização monetária por atraso de valores trabalhistas',
'Aviso prévio indenizado',
'Férias - Dobro na rescisão',
'Férias - Dobro na rescisão',
'Multa rescisória (20% ou 40% do FGTS)',
'Indenização do art. 9º da Lei 7.238/1984',
'Indenização do art. 14 da Lei 5.889/1973 (safrista)',
'Indenização do art. 479 da CLT',
'Indenização por quebra de estabilidade',
'Indenização rescisória - Programa Emergencial de Manutenção do Emprego e da Renda',
'Outras multas ou indenizações',
'Férias - Abono pecuniário (venda de 1/3)',
'Abono legal',
'Auxílio-babá',
'Assistência médica/odontológica',
'Auxílio-creche',
'Auxílio-educação',
'Auxílio - Locais de difícil acesso',
'Ajuda compensatória - Programa Emergencial de Manutenção do Emprego e da Renda',
'Ajuda de custo de transferência',
'13º salário relativo ao aviso prévio indenizado',
'Multa do art. 477 da CLT',
'Tempo de espera do motorista profissional'
]

type Verba = {
  id?: string
  clientId: string
  nome: string
  value: number
}

type VerbasTableProps = {
  idApuracaoCompetencia: string
  cnpj?: string
  isEditable?: boolean
}

export default function VerbasTable({ idApuracaoCompetencia, cnpj = '', isEditable = false }: VerbasTableProps) {
  const { toast } = useToast()
  const user = useCurrentUser()
  const permissions = usePermissions()
  const canManageVerbas = isAdmin(user)
  const canExportVerbas = canManageVerbas || Boolean(permissions?.export_products)

  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const { data: verbasData } = useFetchVerbasByApuracaoCompetencia(idApuracaoCompetencia)
  const [verbas, setVerbas] = useState<Verba[]>([])
  const { mutateAsync: createVerba } = useCreateVerba()
  const { mutateAsync: updateVerba } = useUpdateVerba()
  const { mutate: deleteVerba } = useDeleteVerba()

  useEffect(() => {
    if (verbasData) {
      setVerbas(verbasData.map(mapApiVerbaToRow))
    }
  }, [verbasData])

  const handleSendSave = async () => {
    await handleSaveCreditControls()
  }

  const handleSaveCreditControls = async (event?: FormEvent<HTMLFormElement>) => {
    if (event) event.preventDefault()

    if (!idApuracaoCompetencia) {
      toast({ variant: 'destructive', description: 'Selecione uma competência antes de salvar as verbas.' })
      return
    }

    try {
      const savedRows: Verba[] = []

      for (const verba of verbas) {
        const payload = {
          nome: verba.nome,
          value: Number(verba.value) || 0
        }

        if (verba.id) {
          const original = verbasData?.find(item => item.id === verba.id)
          const changed = original?.nome !== payload.nome || Number(original?.value) !== payload.value
          const saved = changed ? await updateVerba({ id: verba.id, ...payload }) : original

          if (saved) {
            savedRows.push(mapApiVerbaToRow(saved))
          }
          continue
        }

        const saved = await createVerba({
          id_apuracao_competencia: idApuracaoCompetencia,
          ...payload
        })
        savedRows.push(mapApiVerbaToRow(saved))
      }

      setVerbas(savedRows)
      setMessage({ type: 'success', text: 'A verba foi salva com sucesso!' })
      setIsDialogOpen(true)
    } catch {
      toast({ variant: 'destructive', description: 'Houve um erro ao salvar a verba.' })
    }
  }

  const handleDeleteVerba = (verba: Verba) => {
    if (!verba.id) {
      setVerbas(prev => prev.filter(v => v.clientId !== verba.clientId))
      return
    }

    deleteVerba(
      {
        id: verba.id,
        id_apuracao_competencia: idApuracaoCompetencia
      },
      {
        onSuccess: () => {
          setVerbas(prev => prev.filter(v => v.clientId !== verba.clientId))
        },
        onError: () => {
          toast({ variant: 'destructive', description: 'Houve um erro ao excluir a verba.' })
        }
      }
    )
  }

  const handleAddVerba = () => {
    setVerbas(prev => [...prev, { clientId: Date.now().toString(), nome: '', value: 0 }])
  }

  const handleUpdateVerba = (clientId: string, field: keyof Pick<Verba, 'nome' | 'value'>, value: string | number) => {
    setVerbas(prev => prev.map(v => (v.clientId === clientId ? { ...v, [field]: value } : v)))
  }

  const formatCurrency = (value: number | bigint) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)

  const { buscarCNPJ } = useUtils()

  const handleClickPDF = async () => {
    const response = await buscarCNPJ(cnpj)
    handleDownloadPDFVerbas({
      cnpj,
      nomeEmpresa: response.nome,
      periodoInicial: '',
      periodoFinal: '',
      verbas: verbas.map(({ nome, value }) => ({
        verba: nome,
        valor: typeof value === 'number' ? value : parseFloat(value as any)
      }))
    })
  }
  return (
    <div className="bg-background  mx-auto p-6 mt-4  rounded-lg shadow-2lg">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-base md:text-xl font-bold">Relatório de Apuração</h2>
        {(canManageVerbas || canExportVerbas) && (
          <div className="space-x-2">
            {canExportVerbas && (
              <Button
                variant="outline"
                size="icon"
                className="bg-primary text-primary-foreground hover:bg-primary-hover"
                onClick={handleClickPDF}
              >
                <DownloadIcon className="h-4 w-4" />
              </Button>
            )}

            {canManageVerbas && (
              <Button
                onClick={handleAddVerba}
                variant="outline"
                size="icon"
                className="bg-primary text-primary-foreground hover:bg-primary-hover"
                disabled={!canManageVerbas || !isEditable}
              >
                <PlusIcon className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </div>
      <div className="max-h-[600px] w-full overflow-hidden border border-gray-200 rounded-lg shadow-lg">
        {/* 🔹 Container para permitir rolagem horizontal */}
        <div className="w-full overflow-x-auto">
          <Table className="w-full min-w-[600px]">
            {/* 🔹 Cabeçalho fixo */}
            <TableHeader className="bg--secondary">
              <TableRow>
                <TableHead className="text-foreground w-20">Ações</TableHead>
                <TableHead className="text-foreground w-40">Verba</TableHead>
                <TableHead className="text-foreground w-40">Valor</TableHead>
              </TableRow>
            </TableHeader>
          </Table>
        </div>

        {/* 🔹 Corpo Rolável (Vertical e Horizontal) */}
        <div className="max-h-[600px] overflow-auto">
          <div className="w-full min-w-[600px]">
            <Table className="w-full">
              <TableBody>
                {verbas.map(verba => (
                  <TableRow key={verba.clientId}>
                    <TableCell className="w-20">
                      <ButtonConfirmDelet
                        confirmText="Excluir verba?"
                        successBtnText="Excluir"
                        onSuccess={() => handleDeleteVerba(verba)}
                        isDisabled={!canManageVerbas}
                      />
                    </TableCell>

                    <TableCell className="w-40 relative">
                      <select
                        className="w-full bg-input text-foreground rounded px-2 py-1"
                        value={verba.nome}
                        onChange={e => handleUpdateVerba(verba.clientId, 'nome', e.target.value)}
                        disabled={!canManageVerbas}
                      >
                        <option value="">Selecione a verba</option>
                        {verbaOptions.map(option => (
                          <option key={option} value={option}>
                            {option}
                          </option>
                        ))}
                      </select>
                    </TableCell>

                    <TableCell className="w-40">
                      <MaskInputDin
                        className="w-full bg-input text-foreground"
                        value={typeof verba.value === 'number' ? verba.value : parseFloat(String(verba.value)) || 0}
                        onChange={(num: number) => handleUpdateVerba(verba.clientId, 'value', num)}
                        disabled={!canManageVerbas}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </div>
      </div>

      <FeedbackDialog isOpen={isDialogOpen} onOpenChange={setIsDialogOpen} message={message} />

      <div className="mt-4">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Total:</h3>
          <p>{formatCurrency(verbas.reduce((sum, verba) => sum + (Number(verba.value) || 0), 0))}</p>
        </div>
      </div>

      {canManageVerbas && (
        <div className="flex items-center justify-end space-x-2 py-4 border-t">
          <Button
            className="bg-primary text-primary-foreground hover:bg-primary-hover px-6"
            onClick={handleSendSave}
            disabled={!canManageVerbas || !isEditable || !idApuracaoCompetencia}
          >
            Salvar
          </Button>
        </div>
      )}
    </div>
  )
}

function mapApiVerbaToRow(verba: VerbaApi): Verba {
  return {
    id: verba.id,
    clientId: verba.id,
    nome: verba.nome,
    value: Number(verba.value) || 0
  }
}
